import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Pagination,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { adminService } from "../../services/adminService";
import ReportFilters, { today, monthAgo } from "./ReportFilters";
import { formatShowDateTimeIST } from "../../utils/dateTimeIST";
import toast from "react-hot-toast";

const fmt = (v: any) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const BookingsReport: React.FC = () => {
  const [filters, setFilters] = useState({
    dateFrom: monthAgo,
    dateTo: today,
    cinemaId: "",
    paymentMethod: "",
  });
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cinemas, setCinemas] = useState<Array<{ id: string; name: string }>>(
    [],
  );

  const limit = 50;

  useEffect(() => {
    adminService
      .getCinemas()
      .then((r: any) => {
        const list = r?.data || [];
        setCinemas(
          list.map((c: any) => ({ id: c.vistacinemaid || c.id, name: c.name })),
        );
      })
      .catch(() => setCinemas([]));
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const r: any = await adminService.getBookingsReport({
        ...filters,
        page,
        limit,
      });
      setRows(r?.data || []);
      setTotal(r?.pagination?.total || 0);
    } catch (e: any) {
      setError(e?.message || "Failed to load bookings report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onApply = () => {
    setPage(1);
    fetchData();
  };

  const onReset = () => {
    const reset = {
      dateFrom: monthAgo,
      dateTo: today,
      cinemaId: "",
      paymentMethod: "",
    };
    setFilters(reset);
    setPage(1);
    setTimeout(fetchData, 0);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await adminService.exportBookingsReport(filters);
      toast.success("Excel downloaded");
    } catch (e: any) {
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      <Helmet>
        <title>Bookings Report - MyCinemas Admin</title>
      </Helmet>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">Bookings Report</h2>
          <small className="text-muted">
            One row per booking — full transaction audit
          </small>
        </div>
        <Badge bg="info" className="p-2">
          {total.toLocaleString("en-IN")} total
        </Badge>
      </div>

      <ReportFilters
        {...filters}
        cinemas={cinemas}
        onChange={(p) =>
          setFilters((prev) => ({ ...prev, ...p }))
        }
        onApply={onApply}
        onReset={onReset}
        onExport={onExport}
        loading={loading}
        exporting={exporting}
        showPaymentMethod
      />

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body className="p-0">
          <Table responsive hover size="sm" className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Bkg ID</th>
                <th>Status</th>
                <th>Trans Date</th>
                <th>Cinema</th>
                <th>Movie</th>
                <th>Show Date/Time</th>
                <th>Seats</th>
                <th className="text-end">Ticket</th>
                <th className="text-end">F&amp;B</th>
                <th className="text-end">Conv. Fee</th>
                <th className="text-end">Total</th>
                <th>Payment</th>
                <th>Customer Mobile</th>
                <th>Customer Email</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={14} className="text-center py-4">
                    <Spinner size="sm" /> Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={14} className="text-center py-4 text-muted">
                    No bookings found for the selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r: any) => (
                  <tr key={r.bookingId}>
                    <td className="font-monospace small">{r.bookingId}</td>
                    <td>
                      <Badge
                        bg={
                          r.status === "cancelled"
                            ? "danger"
                            : r.paymentStatus === "manual_refund"
                              ? "warning"
                              : "success"
                        }
                      >
                        {r.status}
                      </Badge>
                    </td>
                    <td className="small">{formatShowDateTimeIST(r.transDate)}</td>
                    <td className="small">{r.cinema}</td>
                    <td className="small">{r.movie}</td>
                    <td className="small">{formatShowDateTimeIST(r.showDateTime)}</td>
                    <td className="small">{r.seats}</td>
                    <td className="text-end small">{fmt(r.ticketAmount)}</td>
                    <td className="text-end small">{fmt(r.fnbAmount)}</td>
                    <td className="text-end small">{fmt(r.convFee)}</td>
                    <td className="text-end small fw-bold">
                      {fmt(r.total)}
                    </td>
                    <td className="small">{r.paymentMethod || "-"}</td>
                    <td className="small">{r.customerMobile || "-"}</td>
                    <td className="small">{r.customerEmail || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First
              disabled={page === 1}
              onClick={() => setPage(1)}
            />
            <Pagination.Prev
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            />
            <Pagination.Item active>{page}</Pagination.Item>
            <Pagination.Next
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
            <Pagination.Last
              disabled={page === totalPages}
              onClick={() => setPage(totalPages)}
            />
          </Pagination>
        </div>
      )}
    </>
  );
};

export default BookingsReport;
